import { createApi } from "@reduxjs/toolkit/query/react";

import type {
  WorkflowRun,
  WorkflowRunStep,
  WorkflowSummary,
} from "../../../../common/types/Workflow";
import { createServiceBaseQuery } from "../../client/serviceBaseQuery";

// Workflow reads go through nav's BFF (/api/workflows), which proxies zeus and
// maps the wire shape to WorkflowSummary. The client just consumes typed DTOs.
export const workflowsService = createApi({
  reducerPath: "workflowsService",
  baseQuery: createServiceBaseQuery("bffApiUri"),
  tagTypes: ["Workflow"],
  refetchOnReconnect: true,
  endpoints: (builder) => ({
    getWorkflows: builder.query<WorkflowSummary[], void>({
      query: () => ({ url: "/api/workflows" }),
      providesTags: [{ type: "Workflow", id: "LIST" }],
    }),
    getWorkflowRuns: builder.query<WorkflowRun[], string>({
      query: (workflowId) => ({ url: `/api/workflows/${workflowId}/runs` }),
      providesTags: (_result, _err, workflowId) => [
        { type: "Workflow", id: `RUNS-${workflowId}` },
      ],
      // Runs are scheduler-driven and change out of band, so never serve a
      // retained cache — drop it on unmount and refetch on every mount (below).
      keepUnusedDataFor: 0,
    }),
    runWorkflow: builder.mutation<void, string>({
      query: (workflowId) => ({
        url: `/api/workflows/${workflowId}/run`,
        method: "POST",
      }),
      invalidatesTags: (_result, _err, workflowId) => [
        { type: "Workflow", id: "LIST" },
        { type: "Workflow", id: `RUNS-${workflowId}` },
      ],
    }),
    updateWorkflowEnabled: builder.mutation<
      void,
      { workflowId: string; enabled: boolean }
    >({
      query: ({ workflowId, enabled }) => ({
        url: `/api/workflows/${workflowId}`,
        method: "PUT",
        body: { enabled },
      }),
      invalidatesTags: [{ type: "Workflow", id: "LIST" }],
    }),
    deleteWorkflow: builder.mutation<void, string>({
      query: (workflowId) => ({
        url: `/api/workflows/${workflowId}`,
        method: "DELETE",
      }),
      async onQueryStarted(workflowId, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch {
          return;
        }
        dispatch(
          workflowsService.util.updateQueryData("getWorkflows", undefined, (draft) => {
            const index = draft.findIndex((item) => item.workflowId === workflowId);
            if (index !== -1) draft.splice(index, 1);
          }),
        );
      },
    }),

    // Unlike getWorkflowRuns above, steps keep the default cache: a finished
    // run's steps are immutable. Freshness for still-running runs is handled
    // at the call site (RunSteps refetches per expand while running).
    getWorkflowRunSteps: builder.query<WorkflowRunStep[], string>({
      query: (runId) => ({ url: `/api/workflows/runs/${runId}/steps` }),
    }),
  }),
});

export const {
  useGetWorkflowsQuery,
  useGetWorkflowRunsQuery,
  useGetWorkflowRunStepsQuery,
  useRunWorkflowMutation,
  useUpdateWorkflowEnabledMutation,
  useDeleteWorkflowMutation,
} = workflowsService;
